import React, { useState, useMemo, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDpHoldingListApi } from "../../api/fakebackend_helper";
import decrypted from "../../helper/decrpted";
import Pagintion from "../../common/Pagination";

const FinancialYearInput = React.forwardRef(({ value, onClick }, ref) => {
  let displayVal = value;
  if (value) {
    const startYear = parseInt(value, 10);
    if (!isNaN(startYear)) {
      displayVal = `${startYear}-${startYear + 1}`;
    }
  }
  return (
    <input
      ref={ref}
      onClick={onClick}
      value={displayVal || ""}
      readOnly
      className="statements-filter-input"
      style={{ cursor: "pointer" }}
    />
  );
});

const DpHoldingsView = ({ clientCode: propClientCode }) => {
  const [selectedDemat, setSelectedDemat] = useState("All");
  const [fYear, setFYear] = useState(new Date(2026, 3, 1));
  const [fromDate, setFromDate] = useState(new Date(2026, 3, 1));
  const [toDate, setToDate] = useState(new Date(2027, 2, 31));
  const [accountType, setAccountType] = useState("All");
  const [scriptSearch, setScriptSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const decryptedResult = useMemo(() => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
      return decrypted(userData);
    } catch (e) {
      return null;
    }
  }, []);

  const clientCode = useMemo(() => {
    return (propClientCode || "").trim();
  }, [propClientCode]);

  const pan = useMemo(() => {
    return decryptedResult?.pan || localStorage.getItem("pan") || "";
  }, [decryptedResult]);

  const dematOptions = useMemo(() => {
    const options = [{ label: "All Demat Accounts", value: "All" }];
    if (clientCode) {
      options.push({ label: `IN300214-${clientCode}`, value: `IN300214${clientCode}` });
    }
    return options;
  }, [clientCode]);

  const [dpHoldingsList, setDpHoldingsList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const fetchDpHoldingsData = useCallback(async () => {
    setLoading(true);
    try {
      const boid = selectedDemat !== "All" ? selectedDemat.replace(/[^0-9A-Za-z]/g, "") : "";

      if (!clientCode) {
        setDpHoldingsList([]);
        setLoading(false);
        return;
      }

      const res = await getDpHoldingListApi({
        clientCode: clientCode,
        pan: pan,
        clientBoid: boid,
      });

      const resData =
        res?.data?.data ||
        res?.data?.list ||
        res?.data?.result ||
        (Array.isArray(res?.data) ? res?.data : null);

      if (resData && Array.isArray(resData) && resData.length > 0) {
        const mapped = resData.map((item, idx) => {
          const qty = parseFloat(item.nholdingqty ?? item.qty ?? item.quantity ?? item.holding_qty ?? 0);
          const ltp = parseFloat(item.closingrate ?? item.ltp ?? item.priceLtp ?? item.price ?? 0);
          const val = parseFloat(item.valuation ?? item.value ?? item.total_val ?? (qty * ltp));

          let rawIsin = (item.cisincode || item.isin || item.isin_code || "-").trim();
          if (!rawIsin || /^X+$/i.test(rawIsin) || rawIsin.toUpperCase().includes("XXXX")) {
            rawIsin = "-";
          }

          return {
            id: item.id || idx + 1,
            dpClientId: item.cclientboid || item.client_boid || item.boid || item.dp_client_id || `IN300214-${clientCode}`,
            stockName: item.cisindescription || item.scrip_name || item.stock_name || item.stockName || item.isin_desc || "-",
            isin: rawIsin,
            quantity: qty,
            priceLtp: ltp,
            valuation: val,
          };
        });
        setDpHoldingsList(mapped);
      } else {
        setDpHoldingsList([]);
      }
    } catch (err) {
      console.error("Error fetching DP Holdings data:", err);
      setDpHoldingsList([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDemat, clientCode, pan]);

  useEffect(() => {
    fetchDpHoldingsData();
  }, [fetchDpHoldingsData]);


  useEffect(() => {
    let result = dpHoldingsList;
    if (selectedDemat !== "All") {
      const cleanSelected = selectedDemat.replace(/[^0-9A-Za-z]/g, "");
      result = result.filter((item) =>
        item.dpClientId.replace(/[^0-9A-Za-z]/g, "").includes(cleanSelected)
      );
    }
    if (scriptSearch.trim() !== "") {
      const searchLower = scriptSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.stockName.toLowerCase().includes(searchLower) ||
          item.isin.toLowerCase().includes(searchLower)
      );
    }
    setFilteredData(result);
    setCurrentPage(1);
  }, [selectedDemat, scriptSearch, dpHoldingsList]);

  const totalValuation = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + item.valuation, 0);
  }, [filteredData]);

  const exportFile = (type) => {
    if (filteredData.length === 0) return;
    if (type === "email") {
      alert("DP Holdings statement sent to registered email successfully!");
      return;
    }
    const csvHeader = ["DP ID / Client ID", "Stock Name", "ISIN", "Quantity", "Price (LTP)", "Valuation"];
    const csvRows = filteredData.map((item) => [
      item.dpClientId,
      `"${item.stockName}"`,
      item.isin,
      item.quantity.toFixed(3),
      item.priceLtp.toFixed(2),
      item.valuation.toFixed(2),
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [csvHeader.join(","), ...csvRows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `DP_Holdings_${type}_${dayjs().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFinancialYearChange = (date) => {
    if (date) {
      setFYear(date);
      const selectedYear = date.getFullYear();
      setFromDate(new Date(selectedYear, 3, 1));
      setToDate(new Date(selectedYear + 1, 2, 31));
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchDpHoldingsData();
  };

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;

  return (
    <div>
      <div className="statements-filter-bar">
        <div className="statements-filter-group">
          <label className="statements-filter-label">Select Demat A/C No.</label>
          <select
            className="statements-filter-input"
            value={selectedDemat}
            onChange={(e) => setSelectedDemat(e.target.value)}
          >
            {dematOptions.map((opt, idx) => (
              <option key={idx} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">Select Financial Year</label>
          <DatePicker
            selected={fYear}
            onChange={handleFinancialYearChange}
            showYearPicker
            dateFormat="yyyy"
            customInput={<FinancialYearInput />}
          />
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">From</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="dd MMM, yyyy"
            className="statements-filter-input"
          />
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">To</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="dd MMM, yyyy"
            className="statements-filter-input"
          />
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">Script Name</label>
          <input
            type="text"
            className="statements-filter-input"
            placeholder="Search stock..."
            value={scriptSearch}
            onChange={(e) => setScriptSearch(e.target.value)}
          />
        </div>

        <button className="btn-statements-search" onClick={handleSearch}>
          Search
        </button>
      </div>

      <div className="statements-meta-info">
        <div>
          <strong>DP ID:</strong> IN300214
        </div>
        <div>
          <strong>LAST UPDATED:</strong> {dayjs().format("DD-MM-YYYY")}
        </div>
      </div>

      <div className="statements-valuation-banner">
        <div className="statements-valuation-label">TOTAL VALUATION</div>
        <div className="statements-valuation-amount">
          ₹ {totalValuation.toFixed(2)}
        </div>
      </div>

      <div className="statements-action-bar">
        <button
          className="btn-statements-export"
          onClick={() => exportFile("excel")}
        >
          ⬇ EXCEL
        </button>
        <button
          className="btn-statements-export"
          onClick={() => exportFile("csv")}
        >
          ⬇ CSV
        </button>
        <button
          className="btn-statements-export"
          onClick={() => exportFile("pdf")}
        >
          ⬇ PDF
        </button>
        <button
          className="btn-statements-export"
          onClick={() => exportFile("email")}
        >
          ✉ Send on Email
        </button>
      </div>

      <div className="statements-table-responsive">
        {loading ? (
          <div className="statements-loading-spinner">Loading DP Holdings...</div>
        ) : (
          <table className="statements-data-table">
            <thead>
              <tr>
                <th>DP ID / Client ID ⇅</th>
                <th>Stock Name ⇅</th>
                <th>ISIN ⇅</th>
                <th className="text-right">Quantity ⇅</th>
                <th className="text-right">Price (LTP) ⇅</th>
                <th className="text-right">Valuation ⇅</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="dp-id-badge">{row.dpClientId}</span>
                    </td>
                    <td className="statements-stock-cell">
                      <a
                        href="#"
                        onClick={(e) => e.preventDefault()}
                        className="statements-stock-link"
                      >
                        {row.stockName}
                      </a>
                    </td>
                    <td>
                      <span className="statements-isin-text">{row.isin}</span>
                    </td>
                    <td className="text-right">{row.quantity.toFixed(3)}</td>
                    <td className="text-right">₹ {row.priceLtp.toFixed(2)}</td>
                    <td className="text-right">
                      <strong>₹ {row.valuation.toFixed(2)}</strong>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="statements-empty-state">
                    No holdings found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {filteredData.length > 0 && (
        <div className="mt-3">
          <Pagintion
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={(pageNumber) => setCurrentPage(pageNumber)}
            listLength={filteredData.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default DpHoldingsView;
